import * as PIXI from 'pixi.js';
import { dispatch, state } from '__common/store';
import { SET_METERS_PER_PIXEL } from 'actions';
import {
  onMouseOutside,
  onStageClick,
  onStageMouseMove,
  onStageMouseUp,
  } from '__editor/panelsEditor/components/stage/stageEvents';
import { isValidCoordinates } from '__editor/panelsEditor/components/background/utils/isValidCoordinates';

let stage: PIXI.Container;

export function setStagedMouseEvents(domElement: HTMLElement) {
  stage
    .on('touchendoutside', onMouseOutside)
    .on('mouseupoutside', onMouseOutside);
  
  domElement.addEventListener('mousedown', onStageClick);
  domElement.addEventListener('mousemove', onStageMouseMove); 
  domElement.addEventListener('mouseup', onStageMouseUp);
  // PIXI v7: Improved touch event handling with proper touch coordinate conversion
  domElement.addEventListener('touchstart', (e) => {
    e.preventDefault();
    if (e.touches && e.touches[0]) {
      const touch = e.touches[0];
      const syntheticEvent = {
        target: e.target,
        clientX: touch.clientX,
        clientY: touch.clientY,
        preventDefault: () => e.preventDefault()
      };
      onStageClick(syntheticEvent as MouseEvent);
    }
  });
  
  domElement.addEventListener('touchmove', (e) => {
    e.preventDefault();
    if (e.touches && e.touches[0]) {
      const touch = e.touches[0];
      const syntheticEvent = {
        target: e.target,
        clientX: touch.clientX,
        clientY: touch.clientY,
        preventDefault: () => e.preventDefault()
      };
      onStageMouseMove(syntheticEvent as MouseEvent);
    }
  });
  
  domElement.addEventListener('touchend', (e) => {
    e.preventDefault();
    if (e.changedTouches && e.changedTouches[0]) {
      const touch = e.changedTouches[0];
      const syntheticEvent = {
        target: e.target,
        clientX: touch.clientX,
        clientY: touch.clientY,
        preventDefault: () => e.preventDefault()
      };
      onStageMouseUp(syntheticEvent as MouseEvent);
    }
  });
}

export const removeChild = (children: PIXI.DisplayObject[], childId: string | number) => children.filter((child: PIXI.Graphics) => child.id !== childId);

/**
 * Initializes the PIXI stage with interactivity and a defined hit area.
 */
export function setupStage(width: number, height: number) {
  stage = new PIXI.Container();
  stage.hitArea = new PIXI.Rectangle(0, 0, width, height);
  stage.interactive = true;
  stage.interactiveChildren = true;
  stage.sortableChildren = true;
  window.stage = stage;
  return stage;
}

/**
 * Ensures children are rendered in the correct order based on their zIndex.
 * Optimization: Performs a single linear pass (O(N)) to check if elements are already in order.
 * This avoids the overhead of the O(N log N) sort operation in most frames where 
 * the relative order of layers hasn't changed.
 */
export function updateLayersOrder(children: PIXI.DisplayObject[]) {
  let needsSort = false;
  for (let i = 1; i < children.length; i++) {
    const prevZIndex = (children[i - 1] as any).zIndex || 0;
    const currZIndex = (children[i] as any).zIndex || 0;
    if (currZIndex < prevZIndex) {
      needsSort = true;
      break;
    }
  }

  if (needsSort) {
    return children.sort((a, b) => {
      const azIndex = (a as any).zIndex || 0;
      const bzIndex = (b as any).zIndex || 0;
      return azIndex - bzIndex;
    });
  }
  return children;
}

export function getStage(): PIXI.Container {
  return stage;
}

export function getMeterPerPixel(): number | null {
  const currentState = state();
  const background = currentState && currentState.background;
  const cords = background && background.cords;
  const zoom = background && Number(background.zoom);

  // Lite unmounts PanelsEditor when leaving a roof. Redux-connected children
  // can still observe the deactivation update while they are being removed,
  // at which point background.cords is intentionally null. Treat that state
  // as "not ready" instead of attempting to read cords.lat.
  if (!isValidCoordinates(cords) || !Number.isFinite(zoom)) {
    return null;
  }

  const mapSize = 1280;
  const newMeter = newMeterPerPixe(cords.lat, zoom, mapSize, mapSize * 2);
  dispatch(SET_METERS_PER_PIXEL(newMeter));
  return newMeter;
}

export function destroyPanelsEditorStage() {
  if (stage) {
    try {
      stage.destroy();
    } catch (_) { /* already destroyed (e.g., HMR replayed unmount) */ }
    stage = null;
    window.stage = null;
  }
}

function newMeterPerPixe(lat, zoom, mapWidth, mapWidthAfterResize) {
  const earthC = 6371000 * 2 * Math.PI;
  const factor = Math.pow(2, 8 + zoom);
  const MeterPerPixel = (Math.cos(lat * Math.PI / 180) * earthC / factor) / 2;
  const MapWidthDistance = mapWidth * MeterPerPixel;
  const ActualMeterPerPixel = MapWidthDistance / mapWidthAfterResize;
  return ActualMeterPerPixel;
}
